import { PaginatedResponse, PaginationParams, User } from '@arpeggi/shared'
import axios from 'axios'
import { silentAuthenticatedGet, useAuth } from '../../hooks/useAuth'
import { objectToFormData } from '../../utils/api'

export interface GetUsersParams extends PaginationParams<Omit<User, 'arpMediaStatistics'>> {
  search?: string
  displayName?: string
  walletAddress?: string
}

export interface CreateUserDto {
  walletAddress: string
  displayName: string
  email: string
  profilePicture?: File
  hcaptchaToken: string
}

export type Parts = Array<keyof Pick<User, 'arpMediaStatistics'>>

export interface UpdateUserDto extends Pick<CreateUserDto, 'displayName' | 'profilePicture'> {
  bio?: string
  openSeaHandle?: string
  soundCloudHandle?: string
}

export const getOneUser = async (
  walletAddressOrId: string | number,
  params?: { parts?: Parts }
) => {
  const { data } = await silentAuthenticatedGet<User>(`/api/v2/users/${walletAddressOrId}`, params)

  return data
}

const getUsers = async (params?: GetUsersParams) => {
  const { data } = await silentAuthenticatedGet<PaginatedResponse<User>>(`/api/v2/users`, params)

  return data
}

export const unauthenticatedCreateUser = async (dto: {
  walletAddress: string
  hcaptchaToken?: string
}) => {
  const { data } = await axios.post<User>(
    '/api/v2/users',
    objectToFormData({ firstName: '', lastName: '', ...dto })
  )

  return data
}

export const useUsersAPI = () => {
  const { authenticatedPost, authenticatedPatch, authenticatedPut } = useAuth()

  const createUser = async (dto: CreateUserDto) => {
    const { data } = await authenticatedPost<User>('/api/v2/users', objectToFormData(dto))

    return data
  }

  const updateUser = async (dto: UpdateUserDto) => {
    const { data } = await authenticatedPatch<User>('/api/v2/users', objectToFormData(dto))

    return data
  }

  const updateUserTwitterHandle = async (tweetLink: string) => {
    const { data } = await authenticatedPut<User>('/api/v2/users/twitter-handle', { tweetLink })

    return data
  }

  return { getOneUser, getUsers, createUser, updateUser, updateUserTwitterHandle }
}

export default useUsersAPI
